import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import { styled } from "@mui/material/styles";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import FormHelperText from "@mui/material/FormHelperText";
import { Editor } from "react-draft-wysiwyg";
import { EditorState, ContentState } from "draft-js";
import { convertToRaw, convertFromHTML } from "draft-js";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import draftToHtml from "draftjs-to-html";

import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";

import Modal from "../../components/Modal";
import { setError, setSuccess } from "../../reducers/appSlice";
import ComboVisibilidad from "../../commons/ComboVisibilidad";
import ComboSiNo from "../../commons/ComboSiNo";
import AutocompleteGrupos from "../../commons/AutocompleteGrupos";

const Row = styled(Grid)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const validationSchema = Yup.object().shape({
  id: Yup.number().nullable(),
  padreId: Yup.number().nullable(),
  activo: Yup.string().required("Requerido"),
  descripcion: Yup.string().required("Requerido"),
  visibilidadId: Yup.string().required("Requerido"),
  accesoSistema: Yup.string().required("Requerido"),
  cuerpoEmail: Yup.string().nullable(),
});

const EditorGrupoModal = ({ open, handleClose, guardar, grupo }) => {
  const dispatch = useDispatch();
  const [saving, setSaving] = useState(false);
  const [editorState, setEditorState] = useState(EditorState.createEmpty());

  const onEditorStateChange = (editorState) => {
    setEditorState(editorState);
    formik.setFieldValue(
      "cuerpoEmail",
      draftToHtml(convertToRaw(editorState.getCurrentContent()))
    );
    //setInforme(draftToHtml(convertToRaw(editorState.getCurrentContent())));
  };

  const handleChangeAutocomplete = (value) => {
    formik.setFieldValue("padreId", value);
  };

  const formik = useFormik({
    initialValues: {
      descripcion: grupo?.descripcion || "",
      padreId: grupo?.padreId || "",
      activo: grupo?.activo || "",
      visibilidadId: grupo?.visibilidadId || "",
      accesoSistema: grupo?.accesoSistema || "",
      id: grupo?.codigo,
      cuerpoEmail: grupo?.cuerpoEmail,
    },
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      try {
        setSaving(true);
        await guardar({ ...values, nombre: values.descripcion?.toUpperCase() });
        dispatch(setSuccess("Grupo guardado"));
        handleClose();
      } catch (error) {
        dispatch(setError(error.message));
      } finally {
        setSaving(false);
      }
    },
    onReset: () => {},
  });

  useEffect(() => {
    if (!open) {
      setSaving(false);
      formik.handleReset();
      setEditorState(EditorState.createEmpty());
    }
  }, [open]);

  useEffect(() => {
    if (!grupo) return;
    const contentDataState = ContentState.createFromBlockArray(
      convertFromHTML(grupo.cuerpoEmail || "")
    );
    const editorDataState = EditorState.createWithContent(contentDataState);
    setEditorState(editorDataState);
  }, [grupo?.cuerpoEmail]);

  return (
    <Modal open={open} handleClose={handleClose} size="md">
      <form
        id="formGrupo"
        name="formGrupo"
        onSubmit={formik.handleSubmit}
        onReset={formik.handleReset}
        noValidate
        autoComplete="off"
      >
        <Box mb={2}>
          {formik.values.id ? (
            <Typography variant="titulo">Editar grupo:</Typography>
          ) : (
            <Typography variant="titulo">Nuevo grupo:</Typography>
          )}
        </Box>
        <input type="hidden" id="id" name="id" value={formik.values.id} />
        <Row container spacing={1}>
          <Grid item lg={12} xs={12}>
            <TextField
              id="descripcion"
              name="descripcion"
              label="Descripción del grupo"
              variant="outlined"
              size="small"
              fullWidth
              value={formik.values.descripcion.toUpperCase()}
              onChange={formik.handleChange}
              error={
                formik.touched.descripcion && Boolean(formik.errors.descripcion)
              }
              helperText={
                formik.touched.descripcion && formik.errors.descripcion
              }
            />
          </Grid>
        </Row>

        <Row container spacing={1}>
          <Grid item lg={12} xs={12}>
            <AutocompleteGrupos
              name="padreId"
              error={formik.touched.padreId && Boolean(formik.errors.padreId)}
              helperText={formik.touched.padreId && formik.errors.padreId}
              onChange={handleChangeAutocomplete}
              value={formik.values.padreId}
              label="Grupo padre"
              fullWidth
            />
          </Grid>
        </Row>

        <Row container spacing={1}>
          <Grid item lg={4} xs={12}>
            <ComboVisibilidad
              name="visibilidadId"
              error={
                formik.touched.visibilidadId &&
                Boolean(formik.errors.visibilidadId)
              }
              helperText={
                formik.touched.visibilidadId && formik.errors.visibilidadId
              }
              onChange={formik.handleChange}
              value={formik.values.visibilidadId}
              fullWidth
            />
          </Grid>
          <Grid item lg={4} xs={12}>
            <ComboSiNo
              label={"Acceso al sistema"}
              name="accesoSistema"
              error={
                formik.touched.accesoSistema &&
                Boolean(formik.errors.accesoSistema)
              }
              helperText={
                formik.touched.accesoSistema && formik.errors.accesoSistema
              }
              onChange={formik.handleChange}
              value={formik.values.accesoSistema}
              fullWidth
            />
          </Grid>
          <Grid item lg={4} xs={12}>
            <ComboSiNo
              label={"Activo"}
              name="activo"
              error={formik.touched.activo && Boolean(formik.errors.activo)}
              helperText={formik.touched.activo && formik.errors.activo}
              onChange={formik.handleChange}
              value={formik.values.activo}
              fullWidth
            />
          </Grid>
        </Row>

        <Row container spacing={1}>
          <Grid item lg={12} xs={12}>
            <Editor
              editorState={editorState}
              onEditorStateChange={onEditorStateChange}
              wrapperClassName="wrapperClassName"
              toolbar={{
                options: [
                  "inline",
                  "blockType",
                  "fontSize",
                  "list",
                  "textAlign",
                  "history",
                ],
                inline: {
                  options: ["bold", "italic", "underline", "strikethrough"],
                },
              }}
              toolbarStyle={{ border: "1px solid #f1f1f1" }}
              editorStyle={{
                boxSizing: "border-box",
                border: `1px solid ${
                  formik.touched.cuerpoEmail &&
                  Boolean(formik.errors.cuerpoEmail)
                    ? "red"
                    : "#CCCCCC"
                }`,
                padding: "5px",
                borderRadius: ".25rem",
                height: "200px",
              }}
              // readOnly={!formik.values.idDecision}
            />
            <FormHelperText
              error={
                formik.touched.informe && Boolean(formik.errors.cuerpoEmail)
              }
            >
              {formik.touched.cuerpoEmail && formik.errors.cuerpoEmail}
            </FormHelperText>
          </Grid>
        </Row>

        <Box sx={{ display: "flex", flexDirection: "row-reverse" }}>
          <Button
            sx={{ m: 1 }}
            variant="verde"
            color="primary"
            endIcon={<AddIcon />}
            type="submit"
            disabled={saving}
          >
            {saving ? "Guardando..." : "Guardar"}
          </Button>
          <Button
            variant="plata"
            type="button"
            endIcon={<ClearIcon />}
            sx={{ m: 1 }}
            onClick={handleClose}
          >
            Cancelar
          </Button>
        </Box>
      </form>
    </Modal>
  );
};

export default EditorGrupoModal;
